import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle,
  ChevronDown,
  User,
  LogOut,
  Check,
  Menu,
  ArrowLeft
} from 'lucide-react';
import { useLayout } from '../../contexts/LayoutContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Badge, Dropdown, Button } from '../ui';
import { roleInfo, ROLES } from '../../utils/rbac';
import { ViewUserModal } from '../users/ViewUserModal';
import { MOCK_USERS } from '../../utils/mockUsersData';
import { useLanguageNavigation, SUPPORTED_LANGUAGES } from '../routing/LanguageWrapper';
import './Header.css';

export function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const { navigateTo, switchLanguage, currentLang } = useLanguageNavigation();
  const { sidebarCollapsed, toggleMobileMenu } = useLayout();
  const { theme } = useTheme();
  const { user: currentUser, logout, switchRole } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const languageOptions = useMemo(() => ([
    { code: 'en', label: t('languages.en'), flag: '🇬🇧' },
    { code: 'it', label: t('languages.it'), flag: '🇮🇹' },
    { code: 'es', label: t('languages.es'), flag: '🇪🇸' }
  ]), [t]);

  // Generate breadcrumbs from path (excluding language prefix)
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    // Remove language prefix if present
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
      pathSegments.shift();
    }
    if (pathSegments.length === 0) return [];

    return pathSegments.map((segment, index) => {
      const path = '/' + currentLang + '/' + pathSegments.slice(0, index + 1).join('/');
      const segmentToLabelKey = {
        dashboard: 'nav.dashboard',
        analytics: 'nav.analytics',
        map: 'nav.map',
        stations: 'nav.stations',
        reports: 'reports.title',
        settings: 'nav.settings',
        users: 'nav.users',
        help: 'header.help',
        preview: 'reports.preview.title',
        review: 'reports.review.title',
        wizard: 'reports.wizard.title',
      };

      const labelKey = segmentToLabelKey[segment];
      const label = labelKey ? t(labelKey) : segment.charAt(0).toUpperCase() + segment.slice(1);
      return { label, path, isLast: index === pathSegments.length - 1 };
    });
  };

  const breadcrumbs = generateBreadcrumbs();
  const currentLangOption = languageOptions.find(l => l.code === currentLang) || languageOptions[0];

  const { selectedFarm, farms } = useLayout();

  const pagePath = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
      return '/' + pathSegments.slice(1).join('/');
    }
    return location.pathname;
  }, [location.pathname]);
  const isReportWorkspace = /^\/reports\/[^/]+$/.test(pagePath);

  // Page title mapping
  const getPageTitle = () => {
    if (isReportWorkspace) {
      return t('pages.reports.title');
    }
    const pathToTitle = {
      '/dashboard': 'pages.dashboard.title',
      '/analytics': 'pages.analytics.title',
      '/map': 'pages.map.title',
      '/stations': 'pages.stations.title',
      '/reports': 'pages.reports.title',
      '/settings': 'pages.settings.title',
      '/users': 'pages.users.title',
      '/help': 'help.title'
    };
    const titleKey = pathToTitle[pagePath];
    return titleKey ? t(titleKey) : null;
  };

  const pageTitle = getPageTitle();
  const isReportsRoute = pagePath.startsWith('/reports');

  // State to track farm filter for stations page
  const [farmFilter, setFarmFilter] = useState(() => {
    if (pagePath !== '/stations') return null;
    try {
      const stored = localStorage.getItem('stations-farm-filter');
      return stored || 'all';
    } catch {
      return 'all';
    }
  });

  const farmFilterLabel = useMemo(() => {
    if (pagePath !== '/stations') return null;
    if (!farmFilter || farmFilter === 'all') return t('farm.allFarms');
    const farm = farms.find(f => f.id === farmFilter);
    return farm ? farm.name : t('farm.allFarms');
  }, [farmFilter, farms, pagePath, t]);

  // Listen for farm filter changes from StationsPage
  useEffect(() => {
    if (pagePath !== '/stations') {
      setFarmFilter(null);
      return;
    }

    const updateFarmFilter = () => {
      try {
        const stored = localStorage.getItem('stations-farm-filter');
        setFarmFilter(stored || 'all');
      } catch {
        setFarmFilter('all');
      }
    };

    // Listen for custom event from StationsPage
    const handleFarmFilterChange = () => {
      updateFarmFilter();
    };

    // Also listen to storage events (in case of cross-tab updates)
    const handleStorageChange = (e) => {
      if (e.key === 'stations-farm-filter') {
        updateFarmFilter();
      }
    };

    window.addEventListener('stations:farm-filter-changed', handleFarmFilterChange);
    window.addEventListener('storage', handleStorageChange);

    // Initial check
    updateFarmFilter();

    return () => {
      window.removeEventListener('stations:farm-filter-changed', handleFarmFilterChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [pagePath, farms, selectedFarm]);

  // Handle language change via URL navigation
  const handleLanguageChange = (langCode) => {
    switchLanguage(langCode);
    setLangMenuOpen(false);
  };

  const handleRoleChange = (newRole) => {
    switchRole(newRole);
    setUserMenuOpen(false);
  };

  const getUserInitials = () => {
    return `${currentUser.firstName[0]}${currentUser.lastName[0]}`;
  };

  // Transform currentUser to match ViewUserModal expected format
  const viewUserData = useMemo(() => {
    // Try to find matching user in MOCK_USERS by email
    const mockUser = MOCK_USERS.find(u => u.email === currentUser.email);

    if (mockUser) {
      return mockUser;
    }

    // If not found, create a user object with default values
    return {
      id: currentUser.id || '1',
      email: currentUser.email,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      phone: '',
      role: currentUser.role || 'viewer',
      farms: [],
      isActive: true,
      emailVerified: true,
      lastLogin: new Date(),
      createdAt: new Date(),
      customPermissions: currentUser.role === 'custom' ? {
        pages: [],
        analyticsPermissions: { downloadData: false },
        reportsPermissions: { viewReports: false, downloadDocuments: false },
      } : undefined,
    };
  }, [currentUser]);

  return (
    <header className={`header ${sidebarCollapsed ? 'header--sidebar-collapsed' : ''} ${isReportsRoute ? 'header--reports' : ''}`}>
      {/* Mobile Menu Button */}
      <button
        className="header__mobile-menu"
        onClick={toggleMobileMenu}
        aria-label={t('header.toggleMenu')}
      >
        <Menu size={24} />
      </button>

      {/* Left Section - Page Title or Breadcrumbs */}
      <div className="header__left">
        {pagePath === '/help' ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<ArrowLeft size={16} />}
            iconPosition="left"
            onClick={() => navigateTo('/dashboard')}
            className="header__back-button"
          >
            {t('common.backToDashboard')}
          </Button>
        ) : pageTitle ? (
          <div className="header__dashboard-title">
            <h1 className="header__dashboard-title-text">
              {pageTitle}
              {pagePath === '/stations' && farmFilterLabel ? (
                <span className="header__dashboard-title-farm">
                  {' '}· {farmFilterLabel}
                </span>
              ) : selectedFarm ? (
                <span className="header__dashboard-title-farm">
                  {' '}· {selectedFarm.name}
                </span>
              ) : null}
            </h1>
          </div>
        ) : (
          breadcrumbs.length > 0 && (
            <nav className="header__breadcrumbs" aria-label={t('common.a11y.breadcrumb')}>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && <span className="header__breadcrumb-separator">/</span>}
                  {crumb.isLast ? (
                    <span className="header__breadcrumb header__breadcrumb--current">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link to={crumb.path} className="header__breadcrumb">
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )
        )}
      </div>

      {/* Right Section - Actions */}
      <div className="header__right">
        {/* Language Switcher */}
        <div className="header__lang-switcher">
          <button
            className="header__lang-button"
            onClick={() => setLangMenuOpen(!langMenuOpen)}
            data-testid="lang-switcher"
          >
            <span className="header__lang-label">{currentLangOption.label}</span>
            <ChevronDown size={14} />
          </button>

          {langMenuOpen && (
            <>
              <div
                className="header__dropdown-backdrop"
                onClick={() => setLangMenuOpen(false)}
              />
              <div className="header__dropdown header__lang-dropdown">
                {languageOptions.map((langOption) => (
                  <button
                    key={langOption.code}
                    className={`header__dropdown-item ${langOption.code === currentLang ? 'header__dropdown-item--active' : ''
                      }`}
                    onClick={() => handleLanguageChange(langOption.code)}
                    data-testid={`lang-${langOption.code}`}
                  >
                    <span>{langOption.label}</span>
                    {langOption.code === currentLang && (
                      <Check size={16} className="header__dropdown-check" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Help Button */}
        <button
          className="header__action-btn"
          aria-label={t('header.help')}
          onClick={() => navigateTo('/help')}
        >
          <HelpCircle size={20} />
        </button>

        {/* User Menu */}
        <div className="header__user-menu">
          <button
            className="header__user-button"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            data-testid="user-menu"
          >
            <div className="header__user-avatar">
              {getUserInitials()}
            </div>
            <span className="header__user-name">
              {currentUser.firstName} {currentUser.lastName}
            </span>
            <ChevronDown size={16} />
          </button>

          {userMenuOpen && (
            <>
              <div
                className="header__dropdown-backdrop"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="header__dropdown header__user-dropdown">
                {/* User Info Header */}
                <div className="header__user-info">
                  <div className="header__user-avatar header__user-avatar--large">
                    {getUserInitials()}
                  </div>
                  <div className="header__user-details">
                    <div className="header__user-fullname">
                      {currentUser.firstName} {currentUser.lastName}
                    </div>
                    <div className="header__user-email">{currentUser.email}</div>
                    <Badge
                      variant={currentUser.role === ROLES.ADMIN ? 'danger' : 'info'}
                      size="sm"
                      className="header__user-role-badge"
                    >
                      {t(`user.${currentUser.role}`)}
                    </Badge>
                  </div>
                </div>

                <div className="header__dropdown-divider" />

                {/* Menu Items */}
                <button
                  className="header__dropdown-item"
                  data-testid="profile-btn"
                  onClick={() => {
                    setShowViewModal(true);
                    setUserMenuOpen(false);
                  }}
                >
                  <User size={18} />
                  <span>{t('user.myProfile')}</span>
                </button>

                <div className="header__dropdown-divider" />

                {/* Role Switcher */}
                <div className="header__dropdown-section">
                  <div className="header__dropdown-label">
                    {t('user.switchRole')}
                  </div>
                  {Object.values(ROLES)
                    .filter(role => role !== ROLES.SUPERADMIN && role !== ROLES.SUPER_ADMIN) // Hide super admin roles from role switcher
                    .map((role) => (
                      <button
                        key={role}
                        className={`header__dropdown-item header__dropdown-item--indent ${role === currentUser.role ? 'header__dropdown-item--active' : ''
                          }`}
                        onClick={() => handleRoleChange(role)}
                        data-testid={`role-${role}`}
                      >
                        <span>{t(`user.${role}`)}</span>
                        {role === currentUser.role && (
                          <Check size={16} className="header__dropdown-check" />
                        )}
                      </button>
                    ))}
                </div>

                <div className="header__dropdown-divider" />

                {/* Sign Out */}
                <button
                  className="header__dropdown-item header__dropdown-item--danger"
                  onClick={() => {
                    logout();
                    navigateTo('/login');
                  }}
                >
                  <LogOut size={18} />
                  <span>{t('user.signOut')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* View User Modal */}
      <ViewUserModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        user={viewUserData}
      />
    </header>
  );
}
